import React, { useState } from 'react';
import {
  Button, Input, RadioButton, RadioButtonGroup,
} from '@ipc/ipc-components';
import { Link } from '@ipc/ipc-core';

import AlaTableView from '../../components/custom/AlaTableView';
import AlertDialog from '../../components/custom/AlertDialog';
import { ROLE_ADMIN, hasAccess } from '../../utils/access';
import { Table, Download, FormLabel } from '../../elements';

import {
  TopButtonsWrapper, StyledTextarea, InputFileWrapper, AppContentInner, AppContentTitle,
} from '../../styles';
import { primaryButton } from '../../styles/themes';

import { useAlaAggrements } from './hooks';
import {
  TABLE_COLUMNS, COLUMNS, FILE_NAME, TITLE,
} from './constants';
import ConfirmDeleteModal from './components/ConfirmDeleteModal';

// eslint-disable-next-line react/prop-types
export default function AlaLevelAgreements({ newWarning }) {
  const [data, loading, deleteItems, bulkUpdateStatus] = useAlaAggrements();
  const [selected, updateSelected] = useState([]);
  const [comment, updateComment] = useState('');
  const [filterValue, updateFilterValue] = useState('all');
  const [file, updateFile] = useState();
  const [fileValue, updateFileValue] = useState();
  const [shouldRerenderTable, updateShouldRerenderTable] = useState(false);
  const [deleteSelectedModal, updateDeleteSelectedModal] = useState({});
  const [modal, updateModal] = useState({
    open: false,
    confirmTitle: 'Update',
    data: {},
  });
  const itemsStatusUpdate = (items, status) => () => {
    if (hasAccess([ROLE_ADMIN])) {
      updateModal({
        ...modal,
        open: true,
        title: `${status === 'APPROVED' ? 'Approve' : 'Disapprove'} & Confirm`,
        confirmTitle: 'Confirm',
        data: { items, status },
      });
    } else {
      bulkUpdateStatus(items, status);
    }
  };
  const matchCommentPattern = str => /^[ -~\r\n]+$/.test(str);
  const modalConfirmHandler = () => {
    const { items, status } = modal.data;
    if (!matchCommentPattern(comment)) {
      newWarning('Comment should only contain ASCII characters (English letters, numbers and special characters)', 10);
    } else {
      updateModal({
        ...modal,
        open: false,
      });

      bulkUpdateStatus(items, status, file, comment);
    }
  };

  const modalDismissHandler = () => {
    updateModal({
      ...modal,
      data: {},
      open: false,
    });
  };

  const fileChangeHandler = (event) => {
    if (event.target.files && event.target.files.length) {
      updateFile(event.target.files[0]);
      updateFileValue(event.target.value);
    } else {
      updateFile();
      updateFileValue();
    }
  };
  const commentChangeHandler = (event) => {
    updateComment(event.target.value);
  };

  const filterChangeHandler = (event, newValue) => {
    updateFilterValue(newValue);
  };

  const filterData = ({ status = '' }) => filterValue === 'all' || status.toLowerCase().includes(filterValue);

  const actions = item => (
    <>
      <Link to={`/configuration/edit/${item.id}`}>
            Edit
      </Link>
      <Link to={`/configuration/copy/${item.id}`}>
            Copy
      </Link>
      {item.status && item.status.indexOf('Approved') === -1
        && <div onClick={itemsStatusUpdate([item.id], 'APPROVED')} data-testid="approve-action">Approve</div>
      }
      {item.status && item.status.indexOf('Disapproved') === -1
        && <div onClick={itemsStatusUpdate([item.id], 'DISAPPROVED')} data-testid="disapprove-action">Disapprove</div>
      }
      <div onClick={deleteItems([item.id])}>Delete</div>
    </>
  );

  const confirmDeleteModalHandler = (ids) => {
    deleteItems(ids)();
    updateSelected([]);
    updateShouldRerenderTable(!shouldRerenderTable);
    updateDeleteSelectedModal({});
  };

  const dismissDeleteModalHandler = () => {
    updateDeleteSelectedModal({});
  };

  const selectedBulkDelete = ids => () => {
    updateDeleteSelectedModal({ open: true, ids });
  };

  return (
    <AppContentInner>
      <AppContentTitle>Ala Level Agreements</AppContentTitle>
      <ConfirmDeleteModal
        modal={deleteSelectedModal}
        confirmHandler={confirmDeleteModalHandler}
        dismissHandler={dismissDeleteModalHandler}
      />
      <AlertDialog
        title={modal.title}
        open={modal.open}
        confirmTitle={modal.confirmTitle}
        onConfirmation={modalConfirmHandler}
        onDismiss={modalDismissHandler}
      >
        <div className="FormRowInline">
          <FormLabel title="Upload File" labelAbove>
            <InputFileWrapper>
              <Input type="file" onChange={fileChangeHandler} value={fileValue} />
            </InputFileWrapper>
          </FormLabel>

          <FormLabel title="Comment" labelAbove>
            <StyledTextarea onChange={commentChangeHandler} value={comment} />
          </FormLabel>
        </div>
      </AlertDialog>
      <TopButtonsWrapper>
        <RadioButtonGroup name="receptacles" value={filterValue} onChange={filterChangeHandler}>
          <RadioButton value="all" label="All" data-testid="all-radio" />
          <RadioButton value="pending" label="Pending" data-testid="pending-radio" />
        </RadioButtonGroup>
        <Download columns={COLUMNS} fileName={FILE_NAME} title={TITLE} items={data} selected={selected} fontSize={8} />
        <Button onClick={itemsStatusUpdate(selected, 'APPROVED')} disabled={!selected.length} data-testid="selected-approve">Approve</Button>
        <Button onClick={itemsStatusUpdate(selected, 'DISAPPROVED')} disabled={!selected.length} data-testid="selected-disapprove">Disapprove</Button>
        <Button onClick={selectedBulkDelete(selected)} disabled={!selected.length} data-testid="selected-delete">Delete</Button>
        <Link to="/configuration/create">
          <Button theme={primaryButton}>Create ALA Level Agreement</Button>
        </Link>
        <AlaTableView />
      </TopButtonsWrapper>
      <Table
        className="AlaAgreements"
        columns={TABLE_COLUMNS}
        data={data.filter(filterData)}
        loading={loading}
        actions={actions}
        onSelect={updateSelected}
        shouldClearSelected={shouldRerenderTable}
      />
    </AppContentInner>
  );
}
